import React, { useState } from 'react';
import { FaHeart, FaShareAlt } from 'react-icons/fa';
import { resolveProductMediaUrl } from '../../services/apiService';
import { useThemeClasses } from '../../hooks/useThemeClasses';
import { PRODUCT_PLACEHOLDER, onImgErrorToPlaceholder } from '../../utils/placeholderImage';

/**
 * Flipkart-style PDP gallery: vertical thumbs (desktop) + large stage + optional CTAs under image.
 */
export default function PhotoGallery({
  photos = [],
  onWishlist,
  onShare,
  footer = null,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const { isDark } = useThemeClasses();
  const images =
    photos.length > 0
      ? photos.map((u) => resolveProductMediaUrl(u) || PRODUCT_PLACEHOLDER)
      : [PRODUCT_PLACEHOLDER];

  const activeSrc = images[activeIndex] || PRODUCT_PLACEHOLDER;

  const iconBtnClass = isDark
    ? 'flex h-9 w-9 items-center justify-center rounded-full border border-slate-600 bg-slate-800/90 text-slate-300 shadow-sm transition hover:text-rose-400'
    : 'flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:text-rose-500';

  const thumbClass = (active) =>
    isDark
      ? `aspect-square w-full overflow-hidden rounded-sm border-2 bg-slate-800 p-0.5 transition ${
          active ? 'border-[#2874f0]' : 'border-slate-600 hover:border-slate-500'
        }`
      : `aspect-square w-full overflow-hidden rounded-sm border-2 bg-white p-0.5 transition ${
          active ? 'border-[#2874f0]' : 'border-slate-200 hover:border-slate-300'
        }`;

  return (
    <div className={`relative w-full ${isDark ? 'bg-slate-900/40' : 'bg-white'}`}>
      <div className="absolute right-2 top-2 z-10 flex flex-col gap-2 sm:right-3 sm:top-3">
        {onWishlist && (
          <button
            type="button"
            onClick={onWishlist}
            className={iconBtnClass}
            aria-label="Save to wishlist"
          >
            <FaHeart className="text-sm" />
          </button>
        )}
        {onShare && (
          <button
            type="button"
            onClick={onShare}
            className={iconBtnClass}
            aria-label="Share"
          >
            <FaShareAlt className="text-xs" />
          </button>
        )}
      </div>

      <div className="flex gap-2 p-2 sm:gap-3 sm:p-3 lg:p-4">
        {images.length > 1 && (
          <div className="hidden w-[64px] shrink-0 flex-col gap-2 sm:flex lg:w-[72px]">
            {images.map((src, i) => (
              <button
                key={`thumb-${src}-${i}`}
                type="button"
                onClick={() => setActiveIndex(i)}
                className={thumbClass(activeIndex === i)}
                aria-label={`View image ${i + 1}`}
              >
                <img
                  src={src}
                  alt=""
                  className="h-full w-full object-contain"
                  loading="lazy"
                  onError={(e) => onImgErrorToPlaceholder(e, 80, 80, 'Part')}
                />
              </button>
            ))}
          </div>
        )}

        <div className="flex min-h-[280px] flex-1 flex-col sm:min-h-[400px] lg:min-h-[460px]">
          <div
            className={`flex flex-1 items-center justify-center rounded-sm border p-3 sm:p-5 ${
              isDark ? 'border-slate-700/50 bg-slate-950/40' : 'border-slate-100 bg-white'
            }`}
          >
            <img
              src={activeSrc}
              alt={`Product ${activeIndex + 1}`}
              className="max-h-[260px] w-full max-w-full object-contain sm:max-h-[380px] lg:max-h-[440px]"
              loading="eager"
              onError={(e) => onImgErrorToPlaceholder(e, 400, 400, 'Part')}
            />
          </div>

          {images.length > 1 && (
            <div
              className={`mt-2 flex gap-2 overflow-x-auto pb-1 sm:hidden ${
                isDark ? '' : ''
              }`}
            >
              {images.map((src, i) => (
                <button
                  key={`mthumb-${src}-${i}`}
                  type="button"
                  onClick={() => setActiveIndex(i)}
                  className={`h-14 w-14 shrink-0 overflow-hidden rounded-sm border-2 p-0.5 ${
                    isDark ? 'bg-slate-800' : 'bg-white'
                  } ${
                    activeIndex === i
                      ? 'border-[#2874f0]'
                      : isDark
                        ? 'border-slate-600'
                        : 'border-slate-200'
                  }`}
                >
                  <img
                    src={src}
                    alt=""
                    className="h-full w-full object-contain"
                    onError={(e) => onImgErrorToPlaceholder(e, 56, 56, 'Part')}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {footer ? (
        <div
          className={`hidden border-t px-3 py-3 md:block ${
            isDark ? 'border-slate-700/60' : 'border-slate-100'
          }`}
        >
          {footer}
        </div>
      ) : null}
    </div>
  );
}
